import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, setUser, isSuperAdminUser, logout } from '../services/authService';
import { getOrganizationsApi, updateProfileApi } from '../services/apiService';
import './UserSettingsPage.css';

export default function UserSettingsPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [org, setOrg] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editOrganisation, setEditOrganisation] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  const orgName = useMemo(() => (user?.organisationName || '').trim(), [user]);

  useEffect(() => {
    let alive = true;

    const loadOrg = async () => {
      setError('');
      setLoadingOrg(true);
      if (!user?.isSuperAdmin) {
        if (alive) setOrg(null);
        if (alive) setLoadingOrg(false);
        return;
      }
      try {
        const orgs = await getOrganizationsApi();
        const list = Array.isArray(orgs) ? orgs : [];
        const match = orgName ? list.find((o) => (o?.name || '').trim() === orgName) : null;
        if (alive) setOrg(match || null);
      } catch (err) {
        if (alive) setError(err.response?.data?.message || err.message || 'Failed to load organisation details.');
      } finally {
        if (alive) setLoadingOrg(false);
      }
    };

    loadOrg();
    return () => {
      alive = false;
    };
  }, [orgName, user?.isSuperAdmin]);

  const startEdit = () => {
    setEditName(user?.name ?? '');
    setEditOrganisation(user?.organisationName ?? '');
    setEditEmail(user?.email ?? '');
    setProfileError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setProfileError('');
  };

  const saveProfile = async () => {
    const name = (editName ?? '').trim();
    const organisationName = (editOrganisation ?? '').trim();
    if (!name) {
      setProfileError('Name is required.');
      return;
    }
    if (!organisationName) {
      setProfileError('Organisation is required.');
      return;
    }
    const email = (editEmail ?? '').trim().toLowerCase();
    if (!email) {
      setProfileError('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setProfileError('Enter a valid email address.');
      return;
    }
    setSaving(true);
    setProfileError('');
    try {
      const data = await updateProfileApi(name, organisationName, email);
      // Keep the remembered login email in step with the new address.
      if (localStorage.getItem('solarRememberEmail')) {
        localStorage.setItem('solarRememberEmail', data.email);
      }
      setUser({
        id: data.id,
        name: data.name,
        organisationName: data.organisationName || '',
        role: data.role || 'User',
        isSuperAdmin: !!data.isSuperAdmin,
        email: data.email
      });
      // An older backend ignores the email field and still returns success.
      if ((data.email || '').toLowerCase() !== email) {
        setProfileError('Email was not changed: the server is running an older version that does not support email editing.');
        return;
      }
      setEditing(false);
    } catch (err) {
      setProfileError(err.response?.data?.message || err.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="settings-page">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Settings</h1>
          <div className="settings-actions">
            <button type="button" className="settings-btn" onClick={() => navigate(isSuperAdminUser(user) ? '/admin/organizations/create' : '/')}>
              {isSuperAdminUser(user) ? 'Create Organisation' : 'Dashboard'}
            </button>
          </div>
        </div>

        {error && <div className="settings-alert settings-alert-error">{error}</div>}

        <section className="settings-section">
          <div className="settings-section-header">
            <h2 className="settings-section-title">User Details</h2>
            {!editing ? (
              <button type="button" className="settings-btn settings-btn-edit" onClick={startEdit}>Edit</button>
            ) : null}
          </div>
          {profileError && <div className="settings-alert settings-alert-error">{profileError}</div>}
          {editing ? (
            <div className="settings-grid settings-edit-form">
              <div className="settings-row">
                <div className="settings-label">Name</div>
                <div className="settings-value">
                  <input
                    type="text"
                    className="settings-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Name"
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Email</div>
                <div className="settings-value">
                  <input
                    type="email"
                    className="settings-input"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="Email"
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Organisation</div>
                <div className="settings-value">
                  <input
                    type="text"
                    className="settings-input"
                    value={editOrganisation}
                    onChange={(e) => setEditOrganisation(e.target.value)}
                    placeholder="Organisation"
                  />
                </div>
              </div>
              <div className="settings-edit-actions">
                <button type="button" className="settings-btn" onClick={saveProfile} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="settings-btn settings-btn-cancel" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="settings-grid">
              <div className="settings-row">
                <div className="settings-label">Name</div>
                <div className="settings-value">{user?.name || '—'}</div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Email</div>
                <div className="settings-value">{user?.email || '—'}</div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Role</div>
                <div className="settings-value">{user?.role || '—'}</div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Super Admin</div>
                <div className="settings-value">{user?.isSuperAdmin ? 'Yes' : 'No'}</div>
              </div>
              <div className="settings-row">
                <div className="settings-label">Organisation</div>
                <div className="settings-value">{orgName || '—'}</div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

